/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Dialog } from 'primereact/dialog';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { aiModelsService } from '../../services/ai-models.service';
import { projectsService } from '../../services/projects.service';
import type { AiModel, AiModelTestResult } from '../../types';
import { extractErrorMessage } from '../../utils/error-handler';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import { useCanUpdate, useCanDelete, useHasPermission } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { PERMISSIONS } from '../../constants/permissions';
import AdhocRunPanel from '../../components/ai-runs/adhoc-run-panel';
import ConfigurationStatusTag from './configuration-status-tag';

const AiModelView = () => {
  const { id, isReady, navigateToView, navigateToEdit, navigateToList } = useSecureNavigation({
    entity: 'ai-model',
    basePath: '/ai-models',
  });

  const canUpdate = useCanUpdate(JOB_NAMES.AI_MODEL_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.AI_MODEL_MASTER);
  // Ad-hoc text is design mode: the backend accepts it from model or prompt authors only.
  const canAuthorPrompts = useHasPermission(PERMISSIONS.PROMPT_UPDATE);
  const canExecute = canUpdate || canAuthorPrompts;

  const [model, setModel] = useState<AiModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [testModelOpen, setTestModelOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<AiModelTestResult | null>(null);

  /**
   * Call the model once, for real.
   *
   * The Credentials tag only says that nothing required is blank. A key the
   * provider rejects, an Azure deployment name that does not exist and a region
   * that does not serve the model all read as configured and all fail here —
   * which is the point of being able to check before a prompt run does.
   */
  const handleVerify = async () => {
    if (!id) return;
    setVerifying(true);
    try {
      setVerifyResult(await aiModelsService.test(id));
    } catch (err) {
      setVerifyResult({
        modelId: id, testedAt: new Date().toISOString(), ok: false,
        latencyMs: null, reply: null,
        error: extractErrorMessage(err, 'The verification request itself failed'),
      });
    } finally {
      setVerifying(false);
    }
  };

  // Lookup to resolve the owning project id → human name.
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectLabel = (appId?: string | null) => {
    if (!appId) return 'Global / Shared';
    const p = accessibleProjects.find((x) => x._id === appId);
    return p ? (p.appCode ? `${p.name} (${p.appCode})` : p.name) : appId;
  };

  // Set breadcrumb title to show model name instead of UUID
  useBreadcrumbTitle(model?.displayName);

  // The model a fine-tune descends from, so lineage shows a name, not an id.
  const training = model?.modelType === 'fine_tuned' ? model.training : null;
  const baseModelId = training?.baseModelId || null;
  const { data: baseModel, isError: baseModelMissing } = useQuery({
    queryKey: ['ai-models', baseModelId],
    queryFn: () => aiModelsService.getById(baseModelId!),
    enabled: !!baseModelId,
    retry: false,
  });


  useEffect(() => {
    // Wait for ID decryption to complete
    if (!isReady || !id) {
      return;
    }

    const fetchModel = async () => {
      try {
        setLoading(true);
        const data = await aiModelsService.getById(id);
        setModel(data);
      } catch (err: any) {
        setError(extractErrorMessage(err, 'Failed to fetch AI model details'));
      } finally {
        setLoading(false);
      }
    };

    fetchModel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, id]);

  // Handlers
  const handleEdit = () => id && navigateToEdit(id);
  const handleDeleteClick = () => setDeleteDialogOpen(true);

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await aiModelsService.softDelete(id);
      setDeleteDialogOpen(false);
      navigateToList();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete AI model'));
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => setDeleteDialogOpen(false);

  // Loading state
  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  // Error state
  if (error || !model) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'AI model not found'} className="mb-4 w-full" />
        <Button
          label="Back to AI Models"
          icon="pi pi-arrow-left"
          severity="secondary"
          outlined
          onClick={() => navigateToList()}
        />
      </div>
    );
  }

  const caps = model.capabilities;
  const pricing = model.pricing;
  const paramsSchema = (model.paramsSchema && typeof model.paramsSchema === 'object'
    ? model.paramsSchema
    : {}) as Record<string, any>;
  const paramRows = Object.entries(paramsSchema).map(([name, spec]) => {
    const s = (spec && typeof spec === 'object' ? spec : {}) as Record<string, any>;
    return {
      name,
      default: s.default,
      min: s.min,
      max: s.max,
      enum: Array.isArray(s.enum) ? s.enum.join(', ') : undefined,
    };
  });

  return (
    <div className="p-3" data-testid="SCR-AiModel-View">
      {/* Header Section */}
      <ScreenHeader
        icon="pi pi-database"
        title="AI Model Details"
        onBack={() => navigateToList()}
      />

      {/* Model Profile Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <div className="flex items-start gap-2.5">
          {/* Icon */}
          <div className="w-12 h-12 rounded bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
            <i className="pi pi-server text-purple-600 dark:text-purple-400" style={{ fontSize: '1.25rem' }} />
          </div>

          <div>
            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
              <h2 className="text-base font-semibold text-ink">{model.displayName}</h2>
              <Tag value={model.providerType} severity="info" className="text-xs px-1.5 py-0.5" />
              {model.modelType && <Tag value={model.modelType} severity="secondary" className="text-xs px-1.5 py-0.5" />}
              {training?.trainedAt && (
                <Tag value={`Last fine-tuned ${formatTimestamp(training.trainedAt)}`} severity="secondary" className="text-xs px-1.5 py-0.5" />
              )}
              {model.version && <Tag value={model.version} severity="secondary" className="text-xs px-1.5 py-0.5" />}
              {model.isDefault && <Tag value="Default" severity="success" className="text-xs px-1.5 py-0.5" />}
              {model.isDeprecated && <Tag value="Deprecated" severity="warning" className="text-xs px-1.5 py-0.5" />}
            </div>
            <p className="text-xs text-ink-muted font-mono">
              {model.modelId}
            </p>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <ConfigurationStatusTag status={model.configurationStatus} verbose id={model._id} />
              {verifyResult && (
                <span className={`text-xs ${verifyResult.ok ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {verifyResult.ok
                    ? `Responded in ${verifyResult.latencyMs}ms`
                    : verifyResult.error}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Edit/Delete buttons aligned with profile */}
        {!((model as any).isDelete || model.isDeleted) && (
          <div className="flex gap-1.5 sm:self-center">
            {canUpdate && (
              <Button
                label="Verify credentials"
                icon="pi pi-shield"
                severity="secondary"
                loading={verifying}
                onClick={handleVerify}
                size="small"
                data-testid="AiModelView.Button.VerifyCredentials"
              />
            )}
            {canExecute && (
              <Button
                label="Test model"
                icon="pi pi-play"
                severity="success"
                onClick={() => setTestModelOpen(true)}
                size="small"
                data-testid="AiModelView.Button.TestModel"
              />
            )}
            {canUpdate && (
              <Button
                label="Edit"
                icon="pi pi-pencil"
                onClick={handleEdit}
                size="small"
              />
            )}
            {canDelete && (
              <Button
                label="Delete"
                icon="pi pi-trash"
                severity="danger"
                onClick={handleDeleteClick}
                size="small"
              />
            )}
          </div>
        )}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {/* Basic Information */}
        <div className="ds-card p-3">
          <h2 className="text-sm font-semibold text-ink mb-2">Basic Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Display Name</label>
              <p className="text-sm text-ink">{model.displayName || '-'}</p>
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Model ID</label>
              <p className="text-sm text-ink font-mono">{model.modelId || '-'}</p>
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Provider</label>
              <p className="text-sm text-ink">{model.providerType || '-'}</p>
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Model Type</label>
              <p className="text-sm text-ink">{model.modelType || '-'}</p>
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Version</label>
              <p className="text-sm text-ink">{model.version || '-'}</p>
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Project</label>
              <p className="text-sm text-ink">{projectLabel((model as any).appId)}</p>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="ds-card p-3">
          <h2 className="text-sm font-semibold text-ink mb-2">Description</h2>
          <div className="grid grid-cols-1 gap-y-2">
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Description</label>
              <p className="text-sm text-ink">{model.description || '-'}</p>
            </div>
            {model.iconPath && (
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Icon Path</label>
                <p className="text-sm text-ink font-mono">{model.iconPath}</p>
              </div>
            )}
          </div>
        </div>

        {/* Fine-tune lineage */}
        {training && (
          <div className="ds-card p-3" data-testid="AiModelView.Card.FineTune">
            <h2 className="text-sm font-semibold text-ink mb-2">Fine-tune Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Base Model</label>
                {baseModel ? (
                  <button
                    type="button"
                    className="text-sm text-blue-600 hover:underline text-left"
                    onClick={() => navigateToView(baseModel._id)}
                    data-testid="AiModelView.Link.BaseModel"
                  >
                    {baseModel.displayName}
                    <span className="block text-xs text-ink-muted font-mono">{baseModel.modelId}</span>
                  </button>
                ) : (
                  <p className="text-sm text-ink">
                    {baseModelMissing ? 'Not found (deleted or not accessible)' : baseModelId ? '…' : '-'}
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Last Fine-tuned On</label>
                <p className="text-sm text-ink">{training.trainedAt ? formatTimestamp(training.trainedAt) : '-'}</p>
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Training Status</label>
                {training.status ? (
                  <Tag
                    value={training.status}
                    severity={training.status === 'succeeded' ? 'success'
                      : training.status === 'failed' ? 'danger'
                        : training.status === 'cancelled' ? 'warning' : 'info'}
                    className="text-xs px-1.5 py-0.5"
                  />
                ) : <p className="text-sm text-ink">-</p>}
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Fine-tune Job ID</label>
                <p className="text-sm text-ink font-mono break-all">{training.fineTuneJobId || '-'}</p>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-ink-muted block mb-0.5">Training Dataset</label>
                <p className="text-sm text-ink break-all">{training.datasetRef || '-'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Capabilities */}
        {caps && (
          <div className="ds-card p-3">
            <h2 className="text-sm font-semibold text-ink mb-2">Capabilities</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Max Context Tokens</label>
                <p className="text-sm text-ink">{caps.maxContextTokens ?? '-'}</p>
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Max Output Tokens</label>
                <p className="text-sm text-ink">{caps.maxOutputTokens ?? '-'}</p>
              </div>
              <div className="sm:col-span-2 flex flex-wrap gap-1.5">
                {caps.supportsSystemPrompt && <Tag value="System Prompt" severity="info" className="text-xs px-1.5 py-0.5" />}
                {caps.supportsTools && <Tag value="Tools" severity="info" className="text-xs px-1.5 py-0.5" />}
                {caps.supportsStreaming && <Tag value="Streaming" severity="info" className="text-xs px-1.5 py-0.5" />}
                {caps.supportsJsonMode && <Tag value="JSON Mode" severity="info" className="text-xs px-1.5 py-0.5" />}
                {caps.supportsVision && <Tag value="Vision" severity="info" className="text-xs px-1.5 py-0.5" />}
              </div>
            </div>
          </div>
        )}

        {/* Pricing */}
        {pricing && (
          <div className="ds-card p-3">
            <h2 className="text-sm font-semibold text-ink mb-2">Pricing (per 1M tokens)</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Currency</label>
                <p className="text-sm text-ink">{pricing.currency || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Input / M</label>
                <p className="text-sm text-ink">{pricing.inputPerM ?? '-'}</p>
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Output / M</label>
                <p className="text-sm text-ink">{pricing.outputPerM ?? '-'}</p>
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Cache Read / M</label>
                <p className="text-sm text-ink">{pricing.cacheReadPerM ?? '-'}</p>
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Cache Write / M</label>
                <p className="text-sm text-ink">{pricing.cacheWritePerM ?? '-'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Parameters (paramsSchema) */}
        {paramRows.length > 0 && (
          <div className="ds-card lg:col-span-2 p-3">
            <h2 className="text-sm font-semibold text-ink mb-2">
              Parameters (paramsSchema)
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-ink-muted border-b border-line">
                    <th className="py-1.5 pr-4 font-medium">Name</th>
                    <th className="py-1.5 pr-4 font-medium">Default</th>
                    <th className="py-1.5 pr-4 font-medium">Min</th>
                    <th className="py-1.5 pr-4 font-medium">Max</th>
                    <th className="py-1.5 pr-4 font-medium">Enum</th>
                  </tr>
                </thead>
                <tbody>
                  {paramRows.map((r) => (
                    <tr key={r.name} className="border-b border-gray-100 dark:border-gray-700/60">
                      <td className="py-1.5 pr-4 font-mono text-ink">{r.name}</td>
                      <td className="py-1.5 pr-4 text-ink">
                        {r.default === undefined || r.default === null ? '-' : String(r.default)}
                      </td>
                      <td className="py-1.5 pr-4 text-ink">
                        {r.min === undefined || r.min === null ? '-' : String(r.min)}
                      </td>
                      <td className="py-1.5 pr-4 text-ink">
                        {r.max === undefined || r.max === null ? '-' : String(r.max)}
                      </td>
                      <td className="py-1.5 pr-4 text-ink">{r.enum || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <AuditInfoCard
          createdAt={(model as any).createdAt}
          updatedAt={(model as any).updatedAt}
          createdBy={model.createdBy}
          updatedBy={model.updatedBy}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        visible={deleteDialogOpen}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
        message="Are you sure you want to delete this AI model? This is a soft delete and the data will be preserved but marked as deleted."
        testId="AiModelView.Dialog.Delete"
      />

      {/* Test model (ad-hoc) Dialog */}
      <Dialog
        header={`Test model — ${model.displayName}`}
        visible={testModelOpen}
        onHide={() => setTestModelOpen(false)}
        style={{ width: '48rem', maxWidth: '95vw' }}
        maximizable
        dismissableMask
        data-testid="AiModelView.Dialog.TestModel"
      >
        <AdhocRunPanel modelId={model.modelId} />
      </Dialog>
    </div>
  );
};

export default AiModelView;
